import { GridApi } from '@ag-grid-community/core';
import { Flex, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActiveRowCountLabel, formatActiveRowCount, getActiveRowCount } from './active-row-count';

export function ActiveRowCount({ count, label = 'row' }: { count: number | null | undefined; label?: ActiveRowCountLabel }) {
    if (count == null) {
        return null;
    }

    return (
        <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap" flexShrink={0}>
            {formatActiveRowCount(count, label)}
        </Text>
    );
}

export function ActiveRowCountHeader({ count, label }: { count: number | null | undefined; label?: ActiveRowCountLabel }) {
    return (
        <Flex justify="flex-end" px="16px" pb="6px" flexShrink={0}>
            <ActiveRowCount count={count} label={label} />
        </Flex>
    );
}

export function useGridActiveRowCount() {
    const [count, setCount] = useState<number | null>(null);
    const detachRef = useRef<(() => void) | undefined>();

    const attach = useCallback((api: GridApi) => {
        detachRef.current?.();

        const sync = () => {
            const next = getActiveRowCount(api);
            setCount((prev) => (prev === next ? prev : next));
        };

        sync();
        api.addEventListener('filterChanged', sync);
        api.addEventListener('rowDataUpdated', sync);
        if (api.getModel()?.getType() === 'infinite') {
            api.addEventListener('modelUpdated', sync);
        }

        detachRef.current = () => {
            api.removeEventListener('filterChanged', sync);
            api.removeEventListener('rowDataUpdated', sync);
            api.removeEventListener('modelUpdated', sync);
        };
    }, []);

    const detach = useCallback(() => {
        detachRef.current?.();
        detachRef.current = undefined;
    }, []);

    useEffect(() => detach, [detach]);

    const setCountIfChanged = useCallback((next: number | null) => {
        setCount((prev) => (prev === next ? prev : next));
    }, []);

    return { count, attach, detach, setCountIfChanged };
}
